import React, { useRef, useState, VFC } from 'react';
import { useIntl } from 'react-intl';

import { HoverActions } from 'components/ApplicantProfile/HoverAction/HoverActions';
import { messages } from 'containers/ChatAgent/messages';

import { DeleteTopicConfirmationModal } from './DeleteTopicConfirmationModal';
import { KB_TOPICS, displayTopic, isCustomTopic } from './constants';
import { TOPIC_COMPLETE_ICON_SVG, TOPIC_INCOMPLETE_ICON_SVG } from './icons';
import { useAgentContextStyles } from './styles';
import { deriveTopicKey } from './topicKey';

export { KB_TOPICS, isCustomTopic };

// Block a name whose derived key matches an existing topic's canonical name or
// its displayed name — both would upsert into that topic. displayTopic (not raw
// topicDisplayLabel) so a legacy row stored as "Foo (Custom)" also blocks a
// typed "Foo" until the data-fix runs (HRAI-2022).
const collidesWithExisting = (name: string, existing: string[]): boolean => {
  const key = deriveTopicKey(name);
  if (key === undefined) return existing.some(topic => topic === name);
  return existing.some(
    topic =>
      topic === name ||
      deriveTopicKey(topic) === key ||
      deriveTopicKey(displayTopic(topic)) === key,
  );
};

export interface SidebarProps {
  topics: string[];
  completedTopics: Set<string>;
  activeTopicIndex: number;
  onTopicClick: (index: number) => void;
  onAddTopic: (name: string) => void;
  onRenameTopic: (oldName: string, newName: string) => void;
  onDeleteTopic: (name: string) => void;
  disabled?: boolean;
}

export const Sidebar: VFC<SidebarProps> = ({
  topics,
  completedTopics,
  activeTopicIndex,
  onTopicClick,
  onAddTopic,
  onRenameTopic,
  onDeleteTopic,
  disabled = false,
}) => {
  const classes = useAgentContextStyles();
  const intl = useIntl();
  const [isAdding, setIsAdding] = useState(false);
  const [newTopicName, setNewTopicName] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const [editingTopic, setEditingTopic] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const editInputRef = useRef<HTMLInputElement>(null);
  const [topicPendingDelete, setTopicPendingDelete] = useState<string | null>(
    null,
  );

  const completedCount = completedTopics.size;

  const startEditing = (topic: string) => {
    setEditingTopic(topic);
    // Seed the input with the cleaned name so a baked-in "(Custom)" never shows.
    setEditValue(displayTopic(topic));
    setTimeout(() => editInputRef.current?.focus(), 0);
  };

  const cancelEditing = () => {
    setEditingTopic(null);
    setEditValue('');
  };

  const commitEditing = () => {
    if (editingTopic === null) return;
    const trimmed = editValue.trim();
    // Rename only when the value actually diverges from the seeded (cleaned)
    // name — otherwise opening + blurring an unedited "(Custom)" topic would
    // fire a spurious rename just because the seed is stripped.
    if (
      trimmed &&
      trimmed !== displayTopic(editingTopic) &&
      !collidesWithExisting(
        trimmed,
        topics.filter(topic => topic !== editingTopic),
      )
    ) {
      onRenameTopic(editingTopic, trimmed);
    }
    setEditingTopic(null);
    setEditValue('');
  };

  const startAdding = () => {
    setIsAdding(true);
    setNewTopicName('');
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const commitAdd = () => {
    const trimmed = newTopicName.trim();
    if (trimmed && !collidesWithExisting(trimmed, topics)) {
      onAddTopic(trimmed);
    }
    setIsAdding(false);
    setNewTopicName('');
  };

  const cancelAdd = () => {
    setIsAdding(false);
    setNewTopicName('');
  };

  const progressPct =
    topics.length > 0 ? (completedCount / topics.length) * 100 : 0;

  return (
    <div
      className={`${classes.sidebar} ${
        disabled ? classes.sidebarDisabled : ''
      }`}
    >
      {/* Progress widget */}
      <div className={classes.progressWidgetOuter}>
        <div className={classes.progressCard}>
          {/* Progress bar */}
          <div className={classes.progressBarOuter}>
            <div className={classes.progressBarTrack}>
              <div
                className={classes.progressBarFill}
                style={{ width: `${progressPct}%` }}
              />
            </div>
          </div>
          <div className={classes.sidebarProgress}>
            {intl.formatMessage(messages.topicsComplete, {
              count: completedCount,
              total: topics.length,
            })}
          </div>
        </div>
      </div>
      {topics.map((topic, index) => {
        const isComplete = completedTopics.has(topic);
        const isActive = activeTopicIndex === index;
        const isEditing = editingTopic === topic;
        const isCustom = isCustomTopic(topic);

        if (isEditing) {
          return (
            <div key={topic} className={classes.sidebarItem}>
              <span className={classes.sidebarItemIcon}>
                {isComplete
                  ? TOPIC_COMPLETE_ICON_SVG
                  : TOPIC_INCOMPLETE_ICON_SVG}
              </span>
              <input
                ref={editInputRef}
                value={editValue}
                onChange={e => setEditValue(e.target.value)}
                onBlur={commitEditing}
                onClick={e => e.stopPropagation()}
                onKeyDown={e => {
                  if (e.key === 'Enter') commitEditing();
                  if (e.key === 'Escape') cancelEditing();
                }}
                className={classes.sidebarItemEditInput}
              />
            </div>
          );
        }

        return (
          <div
            key={topic}
            className={`${classes.sidebarItem} ${
              isActive ? classes.sidebarItemActive : ''
            }`}
            onClick={() => {
              if (!disabled) onTopicClick(index);
            }}
            role="button"
            tabIndex={disabled ? -1 : 0}
            onKeyDown={e => {
              if (!disabled && (e.key === 'Enter' || e.key === ' '))
                onTopicClick(index);
            }}
          >
            <span className={classes.sidebarItemIcon}>
              {isComplete ? TOPIC_COMPLETE_ICON_SVG : TOPIC_INCOMPLETE_ICON_SVG}
            </span>
            <span className={classes.sidebarItemLabel}>
              {displayTopic(topic)}
            </span>
            {isCustom && !disabled && (
              <span className={classes.sidebarItemActions}>
                <HoverActions
                  variant="icon"
                  editProps={{
                    onClick: e => {
                      e.stopPropagation();
                      startEditing(topic);
                    },
                  }}
                  deleteProps={{
                    onClick: e => {
                      e.stopPropagation();
                      setTopicPendingDelete(topic);
                    },
                  }}
                />
              </span>
            )}
          </div>
        );
      })}

      {isAdding ? (
        <input
          ref={inputRef}
          value={newTopicName}
          onChange={e => setNewTopicName(e.target.value)}
          onBlur={commitAdd}
          onKeyDown={e => {
            if (e.key === 'Enter') commitAdd();
            if (e.key === 'Escape') cancelAdd();
          }}
          placeholder={intl.formatMessage(messages.topicNamePlaceholder)}
          className={classes.newTopicInput}
        />
      ) : (
        <div
          className={classes.addTopic}
          onClick={() => {
            if (!disabled) startAdding();
          }}
          role="button"
          tabIndex={disabled ? -1 : 0}
          onKeyDown={e => {
            if (!disabled && (e.key === 'Enter' || e.key === ' '))
              startAdding();
          }}
        >
          {intl.formatMessage(messages.addTopic)}
        </div>
      )}

      <DeleteTopicConfirmationModal
        open={topicPendingDelete !== null}
        topicName={displayTopic(topicPendingDelete ?? '')}
        onCancel={() => setTopicPendingDelete(null)}
        onConfirm={() => {
          if (topicPendingDelete !== null) onDeleteTopic(topicPendingDelete);
          setTopicPendingDelete(null);
        }}
      />
    </div>
  );
};
